import React, {useState} from 'react';
import Lightbox from 'react-image-lightbox';
import {Col, Row, Button, showConfirm, Icon, Text} from '@mc/ui-v4';
import {useDropzone} from 'react-dropzone';
import {FieldRenderProps} from 'react-final-form';
import {mcDelete, mcNew} from '@mc/react-ui-icons';
import 'react-image-lightbox/style.css';
import './ImageDropzone.scss';

type ImageDropzoneValue = string[];

export type ImageDropzoneProps = {
    onImageUpload: (image: File) => Promise<string>;
    getImageUrl: (imageId: string) => string;

    onFocus?: (val?: ImageDropzoneValue) => void;
    onBlur?: (val?: ImageDropzoneValue) => void;
    onChange?: (val?: ImageDropzoneValue) => void;
    disabled?: boolean;
    readOnly?: boolean;
    value?: ImageDropzoneValue;

    multiple?: boolean;
    maxFiles?: number;
} & FieldRenderProps<ImageDropzoneValue>;

export const ImageDropzone: React.FC<ImageDropzoneProps> = ({
    getImageUrl,
    onImageUpload,
    onChange,
    value,
    readOnly = false,
    disabled = false,
    multiple = false,
    maxFiles = 0,
}) => {
    const [imageIndex, setImageIndex] = useState<number>(0);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    if (!Array.isArray(value)) {
        value = [];
    }

    const readFileSync = async (file: File): Promise<string> => {
        return await new Promise(resolve => {
            const reader = new FileReader();
            reader.onabort = () => console.log('file reading was aborted', file);
            reader.onerror = () => console.log('file reading has failed', file);
            reader.onload = () => {
                onImageUpload(file).then(imageId => {
                    resolve(imageId);
                });
            };
            reader.readAsArrayBuffer(file);
        });
    };

    const handleOnDrop = async (files: File[]) => {
        const fileIds = [...value];
        for (let i = 0; i < files.length; i++) {
            fileIds.push(await readFileSync(files[i]));
        }

        onChange && onChange(fileIds);
    };

    const handleDelete = async (e, fileId: string) => {
        e.stopPropagation();
        const result = await showConfirm({
            title: 'Odstranit fotografii',
            message: 'Opravdu chcete odstranit fotografii?',
            intent: 'danger',
            confirmText: 'Odstranit',
        });
        if (result) {
            onChange(value.filter(x => x !== fileId));
        }
    };

    const {getRootProps, getInputProps} = useDropzone({
        accept: {
            'image/jpg': ['.jpg'],
            'image/jpeg': ['.jpeg'],
            'image/png': ['.png'],
        },
        multiple: multiple,
        maxFiles: maxFiles,
        preventDropOnDocument: true,
        onDrop: handleOnDrop,
    });

    const renderPreviews = value.map((fileId: string, index: number) => (
        <div className={'thumb'} key={fileId}>
            <div
                className={'thumbInner'}
                onClick={() => {
                    setIsOpen(true);
                    setImageIndex(index);
                }}
            >
                <img src={getImageUrl(fileId)} />
                <Button style={'link'} icon={<Icon icon={mcDelete} />} className={'thumbDelete'} onClick={e => handleDelete(e, fileId)} />
            </div>
        </div>
    ));

    return (
        <div className={'image-dropzone'}>
            <Row>
                <Col>
                    {isOpen && (
                        <Lightbox
                            mainSrc={getImageUrl(value[imageIndex])}
                            nextSrc={getImageUrl(value[(imageIndex + 1) % value.length])}
                            prevSrc={getImageUrl(value[(imageIndex + value.length - 1) % value.length])}
                            onCloseRequest={() => setIsOpen(false)}
                            onMovePrevRequest={() => setImageIndex((imageIndex + value.length - 1) % value.length)}
                            onMoveNextRequest={() => setImageIndex((imageIndex + 1) % value.length)}
                            reactModalStyle={{overlay: {zIndex: 42000}}}
                        />
                    )}

                    <aside className={'thumbsContainer'}>
                        {renderPreviews}
                        {readOnly || disabled || (
                            <div {...getRootProps({className: 'dropzone'})}>
                                <input {...getInputProps()} />
                                <Text intent={'primary'} className={'my-auto'}>
                                    <Icon icon={mcNew} />
                                </Text>
                            </div>
                        )}
                    </aside>
                </Col>
            </Row>
        </div>
    );
};
